import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import {
  EXTERNAL_TENDERS,
  GuestBooking,
  OrderConfirmation,
  OrderTiming,
  PREP_MINUTES_MAX,
  ReservationPayment,
  DEFAULT_TILL_TENDERS,
  catalogEditorsOf,
  guestBookingOf,
  orderTimingOf,
  orderingPolicyOf,
  pickupExpiryDaysOf,
  requestHoldHoursOf,
  shopFulfilmentOf,
  shopPaymentOf,
  tillTendersOf,
  tillTipsEnabled,
} from '@yourstore/domain';
import type { CatalogEditors, ExternalTender, TenantConfig } from '@yourstore/domain';
import { useAction, useApi } from '../../context';
import { Confirm, Rich } from '../../bits';
import { Panel } from '../../kit';
import { useT } from '../../../messages';
import type { T } from '../../../messages';
import { moduleName } from './featureList';

/**
 * **The counter's settings, split by where they show up** (console redesign
 * B2) — the old one-card "Settings" form, cut into the four places an owner
 * meets them: at the counter, on the shop's shelf, in the kitchen, on the
 * website. Each place is shown only when the business has the feature it
 * belongs to (principle 6: a spa never sees kitchen or posting questions); a
 * quiet "Show the settings for features that are off" keeps every one of them
 * reachable, for the operator who sets an answer before switching the feature
 * on.
 *
 * What the old card promised still holds, and is why this is written the way
 * it is (`docs/lovespa-gaps-plan.md` B4 and C1, `docs/lovespa-discovery.md`
 * §2.3, spec §3.6):
 *
 *  1. **Only what changed is sent.** An untouched key is left alone — on
 *     `shop_payment` that is the difference between a live arrangement (it
 *     follows whether there is a payment rail) and a frozen one nobody chose.
 *     Each place saves only its own questions, and only the ones that moved.
 *  2. **The controls show the resolved value**, through `@yourstore/domain`'s
 *     readers, and each place says once which of its answers nobody gave.
 *  3. **The refusals stay the server's.** The local checks (an empty till, a
 *     hold outside its range) only spare a round trip.
 *
 * New here: **Tips** (`till.tips`, 0046). It was the one counter answer the
 * old card could not give; the server merges `till`, so sending the switch
 * alone never clobbers the buttons.
 *
 * Saving confirms (principle 5), listing each changed answer in words; the
 * line after it is the old card's summary, built from the server's resolved
 * answer rather than the draft.
 */
interface SettingsDraft {
  tenders: readonly ExternalTender[];
  tips: boolean;
  pickup: boolean;
  ship: boolean;
  payment: ReservationPayment;
  /** Text, not a number: an operator clearing the box mid-edit is not a zero. */
  expiry: string;
  timing: OrderTiming;
  booking: GuestBooking;
  /** Text, for `expiry`'s reason. */
  hold: string;
  confirmation: OrderConfirmation;
  /** Text; `''` is "nobody set one", which is not zero. */
  prep: string;
  /**
   * **Who may change what they sell** (B10) — the owner's own switch in the
   * staff app, set here for a business that asks over the phone. At the
   * counter because that is where the front desk works.
   */
  editors: CatalogEditors;
}

type Field = keyof SettingsDraft;
type Place = 'counter' | 'shop' | 'kitchen' | 'website';

/** Which questions live where, and which feature a place belongs to. */
const PLACES: readonly { id: Place; features: readonly string[]; fields: readonly Field[] }[] = [
  { id: 'counter', features: ['sales'], fields: ['tenders', 'tips', 'editors'] },
  {
    id: 'shop',
    features: ['products', 'fulfillment'],
    fields: ['pickup', 'ship', 'payment', 'expiry'],
  },
  { id: 'kitchen', features: ['ordering'], fields: ['timing', 'confirmation', 'prep'] },
  { id: 'website', features: ['booking_site'], fields: ['booking', 'hold'] },
];

const EXPIRY_MIN = 1;
const EXPIRY_MAX = 90;
const HOLD_MIN = 1;
const HOLD_MAX = 168;

export function TenantSettings({
  tenantId,
  name = '',
  config,
  enabled,
  onChanged,
}: {
  tenantId: string;
  /** The business's name, for the confirm. */
  name?: string;
  config: Partial<TenantConfig> | null;
  /** The features it has. Omitted, every place is shown (a card on its own). */
  enabled?: ReadonlySet<string> | undefined;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [draft, setDraft] = useState<Partial<SettingsDraft>>({});
  const [saved, setSaved] = useState<{ place: Place; text: string } | null>(null);
  const [asking, setAsking] = useState<Place | null>(null);
  const [showAll, setShowAll] = useState(false);

  const fulfilment = shopFulfilmentOf(config);
  const ordering = orderingPolicyOf(config);
  const current: SettingsDraft = {
    tenders: tillTendersOf(config),
    tips: tillTipsEnabled(config),
    pickup: fulfilment.pickup,
    ship: fulfilment.ship,
    payment: shopPaymentOf(config),
    expiry: String(pickupExpiryDaysOf(config)),
    timing: orderTimingOf(config),
    booking: guestBookingOf(config),
    hold: String(requestHoldHoursOf(config)),
    confirmation: ordering.orderConfirmation,
    prep: ordering.defaultPrepMinutes === null ? '' : String(ordering.defaultPrepMinutes),
    editors: catalogEditorsOf(config),
  };
  const value: SettingsDraft = { ...current, ...draft };

  /** Whether the tenant has actually answered, as opposed to resolving to this. */
  const answered = (key: string): boolean => config !== null && key in config;
  const inBlock = (block: 'till' | 'ordering', field: string): boolean => {
    const raw: unknown = config?.[block];
    return typeof raw === 'object' && raw !== null && field in raw;
  };
  /**
   * Which questions somebody has answered. An unanswered one still shows the
   * value the business gets (the reader's default); the place says once, under
   * its questions, which ones those are — not a "not set" after every hint.
   */
  const isSet: Record<Field, boolean> = {
    tenders: answered('till') && inBlock('till', 'tenders'),
    tips: inBlock('till', 'tips'),
    editors: answered('catalog_editors'),
    pickup: answered('shop_fulfilment'),
    ship: answered('shop_fulfilment'),
    payment: answered('shop_payment'),
    expiry: answered('shop_pickup_expiry_days'),
    timing: answered('order_timing'),
    confirmation: inBlock('ordering', 'order_confirmation'),
    prep: inBlock('ordering', 'default_prep_minutes'),
    booking: answered('guest_booking'),
    hold: answered('request_hold_hours'),
  };
  /** The hold is drawn only when the website can make a request. */
  const drawn = (field: Field): boolean => field !== 'hold' || value.booking !== 'open';
  const unanswered = (id: Place): Field[] =>
    placeOf(id).fields.filter((f) => drawn(f) && !isSet[f]);

  const changed = (field: Field): boolean =>
    field === 'tenders'
      ? value.tenders.length !== current.tenders.length ||
        value.tenders.some((x, i) => current.tenders[i] !== x)
      : value[field] !== current[field];

  const patch = (next: Partial<SettingsDraft>): void => {
    action.clear();
    setSaved(null);
    setDraft((d) => ({ ...d, ...next }));
  };

  const toggleTender = (tender: ExternalTender): void => {
    const on = value.tenders.includes(tender);
    patch({
      tenders: on
        ? value.tenders.filter((x) => x !== tender)
        : EXTERNAL_TENDERS.filter((x) => x === tender || value.tenders.includes(x)),
    });
  };

  const expiryDays = Number(value.expiry);
  const holdHours = Number(value.hold);
  const prepMinutes = Number(value.prep);
  const valid: Record<Place, boolean> = {
    counter: value.tenders.length > 0,
    shop: Number.isInteger(expiryDays) && expiryDays >= EXPIRY_MIN && expiryDays <= EXPIRY_MAX,
    kitchen:
      value.prep.trim() === '' ||
      (Number.isInteger(prepMinutes) && prepMinutes >= 0 && prepMinutes <= PREP_MINUTES_MAX),
    website: Number.isInteger(holdHours) && holdHours >= HOLD_MIN && holdHours <= HOLD_MAX,
  };

  const placeOf = (id: Place): (typeof PLACES)[number] =>
    PLACES.find((p) => p.id === id) ?? (PLACES[0] as (typeof PLACES)[number]);
  const dirty = (id: Place): boolean => placeOf(id).fields.some(changed);

  /** Only this place's questions, and only the ones that moved. */
  const bodyFor = (id: Place): Parameters<typeof api.setSettings>[1] => {
    const has = (f: Field): boolean => placeOf(id).fields.includes(f) && changed(f);
    return {
      ...(has('tenders') || has('tips')
        ? {
            till: {
              ...(has('tenders') ? { tenders: [...value.tenders] } : {}),
              ...(has('tips') ? { tips: value.tips } : {}),
            },
          }
        : {}),
      ...(has('pickup') || has('ship')
        ? { shop_fulfilment: { pickup: value.pickup, ship: value.ship } }
        : {}),
      ...(has('payment') ? { shop_payment: value.payment } : {}),
      ...(has('expiry') ? { shop_pickup_expiry_days: expiryDays } : {}),
      ...(has('timing') ? { order_timing: value.timing } : {}),
      ...(has('booking') ? { guest_booking: value.booking } : {}),
      ...(has('hold') ? { request_hold_hours: holdHours } : {}),
      ...(has('editors') ? { catalog_editors: value.editors } : {}),
      // One block, only the fields that moved; a cleared box sends `null`,
      // which the route removes from the block — "nobody set one" again.
      ...(has('confirmation') || has('prep')
        ? {
            ordering: {
              ...(has('confirmation') ? { order_confirmation: value.confirmation } : {}),
              ...(has('prep')
                ? { default_prep_minutes: value.prep.trim() === '' ? null : prepMinutes }
                : {}),
            },
          }
        : {}),
    };
  };

  /** Each changed answer as "**Question** answer." — the confirm's body. */
  const changesIn = (id: Place): string =>
    placeOf(id)
      .fields.filter(changed)
      .map((f) =>
        t('setup.settings.change', { question: question(t, f), answer: answer(t, f, value) }),
      )
      .join(' ');

  const save = async (id: Place): Promise<void> => {
    const ok = await action.run(async () => {
      const got = await api.setSettings(tenantId, bodyFor(id));
      // The server's resolved answer, quoted rather than the draft.
      setSaved({
        place: id,
        text: t('settings.saved', {
          tenders: (got.till.tenders ?? DEFAULT_TILL_TENDERS).join(', '),
          payment: paymentLabel(t, got.shop_payment),
          booking: bookingLabel(t, got.guest_booking),
        }),
      });
    });
    if (ok) {
      setAsking(null);
      const fields = placeOf(id).fields;
      setDraft((d) => {
        const next = { ...d };
        for (const f of fields) delete next[f];
        return next;
      });
      onChanged();
    }
  };

  const has = (id: Place): boolean =>
    enabled === undefined || placeOf(id).features.some((f) => enabled.has(f));
  const visible = PLACES.filter((p) => showAll || has(p.id));
  const hiddenAny = PLACES.some((p) => !has(p.id));

  const footer = (id: Place): ReactNode => (
    <div className="setup-actions">
      {dirty(id) ? (
        <button
          type="button"
          className="button button--quiet"
          disabled={action.busy}
          onClick={() => {
            action.clear();
            setDraft((d) => {
              const next = { ...d };
              for (const f of placeOf(id).fields) delete next[f];
              return next;
            });
          }}
        >
          {t('setup.settings.undo')}
        </button>
      ) : null}
      <button
        type="button"
        className="button button--primary"
        disabled={!dirty(id) || !valid[id] || action.busy}
        onClick={() => {
          action.clear();
          setAsking(id);
        }}
      >
        {t('setup.settings.save')}
      </button>
    </div>
  );

  const frame = (id: Place, children: ReactNode): ReactNode => (
    <Panel key={id} title={placeTitle(t, id)} className="setup-settings">
      {has(id) ? null : (
        <p className="faint">
          {t('setup.settings.forFeature', {
            feature: placeOf(id)
              .features.map((f) => moduleName(t, f))
              .join(' / '),
          })}
        </p>
      )}
      {saved?.place === id ? <p className="ok">{saved.text}</p> : null}
      <div className="settings-list">{children}</div>
      {unanswered(id).length === 0 ? null : (
        <p className="faint">
          {t('setup.settings.notSet', {
            list: unanswered(id)
              .map((f) => t('setup.settings.quoted', { question: question(t, f) }))
              .join(', '),
          })}
        </p>
      )}
      {footer(id)}
    </Panel>
  );

  const places: Record<Place, () => ReactNode> = {
    counter: () =>
      frame(
        'counter',
        <>
          <Setting label={t('setup.settings.tenders')} hint={t('setup.settings.tendersHint')} group>
            <div className="choice">
              {EXTERNAL_TENDERS.map((tender) => (
                <label key={tender}>
                  <input
                    type="checkbox"
                    aria-label={t('settings.tenderLabel', { tender })}
                    checked={value.tenders.includes(tender)}
                    onChange={() => toggleTender(tender)}
                  />
                  <span>{tenderWord(t, tender)}</span>
                </label>
              ))}
            </div>
            {valid.counter ? null : <p className="failure__hint">{t('settings.noTenders')}</p>}
          </Setting>
          <Setting label={t('setup.settings.tips')} hint={t('setup.settings.tipsHint')} group>
            <div className="choice">
              {[true, false].map((on) => (
                <label key={String(on)}>
                  <input
                    type="radio"
                    name={`${tenantId}-tips`}
                    checked={value.tips === on}
                    onChange={() => patch({ tips: on })}
                  />
                  <span>{on ? t('setup.settings.on') : t('setup.settings.off')}</span>
                </label>
              ))}
            </div>
          </Setting>
          <Setting label={t('setup.settings.editors')} hint={t('setup.settings.editorsHint')} group>
            <div className="choice">
              {(['owner', 'owner_and_staff'] as const).map((editors) => (
                <label key={editors}>
                  <input
                    type="radio"
                    name={`${tenantId}-editors`}
                    checked={value.editors === editors}
                    onChange={() => patch({ editors })}
                  />
                  <span>{editorsLabel(t, editors)}</span>
                </label>
              ))}
            </div>
          </Setting>
        </>,
      ),
    shop: () =>
      frame(
        'shop',
        <>
          <Setting label={t('settings.pickup')} hint={t('settings.pickupHint')}>
            <select
              value={value.pickup ? 'yes' : 'no'}
              onChange={(e) => patch({ pickup: e.target.value === 'yes' })}
            >
              <option value="yes">{t('settings.pickup.yes')}</option>
              <option value="no">{t('settings.pickup.no')}</option>
            </select>
          </Setting>
          <Setting label={t('settings.ship')} hint={t('settings.shipHint')}>
            <select
              value={value.ship ? 'yes' : 'no'}
              onChange={(e) => patch({ ship: e.target.value === 'yes' })}
            >
              <option value="yes">{t('settings.ship.yes')}</option>
              <option value="no">{t('settings.ship.no')}</option>
            </select>
          </Setting>
          <Setting label={t('settings.payment')} hint={t('settings.paymentHint')}>
            <select
              value={value.payment}
              onChange={(e) => patch({ payment: e.target.value as ReservationPayment })}
            >
              {ReservationPayment.options.map((option) => (
                <option key={option} value={option}>
                  {paymentLabel(t, option)}
                </option>
              ))}
            </select>
          </Setting>
          <Setting label={t('settings.expiry')} hint={t('settings.expiryHint')}>
            <input
              type="number"
              min={EXPIRY_MIN}
              max={EXPIRY_MAX}
              value={value.expiry}
              aria-invalid={!valid.shop}
              onChange={(e) => patch({ expiry: e.target.value })}
            />
          </Setting>
        </>,
      ),
    kitchen: () =>
      frame(
        'kitchen',
        <>
          <Setting label={t('settings.timing')} hint={t('settings.timingHint')}>
            <select
              value={value.timing}
              onChange={(e) => patch({ timing: e.target.value as OrderTiming })}
            >
              {OrderTiming.options.map((option) => (
                <option key={option} value={option}>
                  {timingLabel(t, option)}
                </option>
              ))}
            </select>
          </Setting>
          <Setting label={t('settings.confirmation')} hint={t('settings.confirmationHint')}>
            <select
              value={value.confirmation}
              onChange={(e) => patch({ confirmation: e.target.value as OrderConfirmation })}
            >
              {OrderConfirmation.options.map((option) => (
                <option key={option} value={option}>
                  {confirmationLabel(t, option)}
                </option>
              ))}
            </select>
          </Setting>
          <Setting label={t('settings.prep')} hint={t('settings.prepHint')}>
            <input
              type="number"
              min={0}
              max={PREP_MINUTES_MAX}
              value={value.prep}
              placeholder={t('settings.prepNone')}
              onChange={(e) => patch({ prep: e.target.value })}
              aria-invalid={!valid.kitchen}
            />
          </Setting>
        </>,
      ),
    website: () =>
      frame(
        'website',
        <>
          <Setting label={t('settings.booking')} hint={t('settings.bookingHint')}>
            <select
              value={value.booking}
              onChange={(e) => patch({ booking: e.target.value as GuestBooking })}
            >
              {GuestBooking.options.map((option) => (
                <option key={option} value={option}>
                  {bookingLabel(t, option)}
                </option>
              ))}
            </select>
          </Setting>
          {/*
           * Drawn only where it can mean something: under `open` nothing ever
           * becomes a request, so a hold would be a number with no subject.
           */}
          {value.booking === 'open' ? null : (
            <Setting label={t('settings.hold')} hint={t('settings.holdHint')}>
              <input
                type="number"
                min={HOLD_MIN}
                max={HOLD_MAX}
                value={value.hold}
                onChange={(e) => patch({ hold: e.target.value })}
                aria-invalid={!valid.website}
              />
            </Setting>
          )}
        </>,
      ),
  };

  return (
    <>
      {visible.length === 0 ? <p className="faint">{t('setup.settings.none')}</p> : null}
      {visible.map((p) => places[p.id]())}
      {hiddenAny ? (
        <div>
          <button
            type="button"
            className="linkish"
            aria-expanded={showAll}
            onClick={() => setShowAll((was) => !was)}
          >
            {showAll ? t('setup.settings.hideAll') : t('setup.settings.showAll')}
          </button>
        </div>
      ) : null}

      {asking !== null ? (
        <Confirm
          title={t('setup.settings.confirm.title', { section: placeTitle(t, asking) })}
          consequence={`${t('setup.settings.confirm.lead', { business: name === '' ? tenantId : name })} ${changesIn(asking)}`}
          confirmLabel={t('setup.settings.confirm.save')}
          tone="primary"
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void save(asking)}
          onCancel={() => {
            action.clear();
            setAsking(null);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* One question                                                        */
/* ------------------------------------------------------------------ */

/**
 * The mockup's `.setting` row: the question and its hint on the left, the
 * answer on the right. A single control sits inside a `<label>` that is the
 * whole row, so the question names it ("Can guests collect in person?" finds
 * its select) — `Field`'s arrangement, laid out as the mockup's two columns.
 * A set of checkboxes or radios is a labelled group instead, each input
 * keeping its own name.
 */
function Setting({
  label,
  hint,
  group = false,
  children,
}: {
  label: string;
  hint: string;
  group?: boolean;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const text = (
    <div>
      <div className="setting__q" id={`${id}-q`}>
        {label}
      </div>
      <div className="setting__h">
        <Rich text={hint} />
      </div>
    </div>
  );
  return group ? (
    <div className="setting" role="group" aria-labelledby={`${id}-q`}>
      {text}
      <div className="setting__a">{children}</div>
    </div>
  ) : (
    <label className="setting">
      {text}
      <div className="setting__a">{children}</div>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Words                                                               */
/* ------------------------------------------------------------------ */

function placeTitle(t: T, place: Place): string {
  switch (place) {
    case 'counter':
      return t('setup.settings.counter');
    case 'shop':
      return t('setup.settings.shop');
    case 'kitchen':
      return t('setup.settings.kitchen');
    case 'website':
      return t('setup.settings.website');
  }
}

function tenderWord(t: T, tender: string): string {
  switch (tender) {
    case 'cash':
      return t('setup.tender.cash');
    case 'check':
      return t('setup.tender.check');
    // Brand names, the same in every language — not catalog entries.
    case 'venmo':
      return 'Venmo';
    case 'paypal':
      return 'PayPal';
    case 'card':
      return t('setup.tender.card');
    case 'other':
      return t('setup.tender.other');
    default:
      return tender;
  }
}

function question(t: T, field: Field): string {
  switch (field) {
    case 'tenders':
      return t('setup.settings.tenders');
    case 'tips':
      return t('setup.settings.tips');
    case 'pickup':
      return t('settings.pickup');
    case 'ship':
      return t('settings.ship');
    case 'payment':
      return t('settings.payment');
    case 'expiry':
      return t('settings.expiry');
    case 'timing':
      return t('settings.timing');
    case 'booking':
      return t('settings.booking');
    case 'hold':
      return t('settings.hold');
    case 'confirmation':
      return t('settings.confirmation');
    case 'prep':
      return t('settings.prep');
    case 'editors':
      return t('setup.settings.editors');
  }
}

function answer(t: T, field: Field, v: SettingsDraft): string {
  switch (field) {
    case 'tenders':
      return v.tenders.map((x) => tenderWord(t, x)).join(', ');
    case 'tips':
      return v.tips ? t('setup.settings.on') : t('setup.settings.off');
    case 'pickup':
      return v.pickup ? t('settings.pickup.yes') : t('settings.pickup.no');
    case 'ship':
      return v.ship ? t('settings.ship.yes') : t('settings.ship.no');
    case 'payment':
      return paymentLabel(t, v.payment);
    case 'expiry':
      return v.expiry;
    case 'timing':
      return timingLabel(t, v.timing);
    case 'booking':
      return bookingLabel(t, v.booking);
    case 'hold':
      return v.hold;
    case 'confirmation':
      return confirmationLabel(t, v.confirmation);
    case 'prep':
      return v.prep.trim() === '' ? t('setup.settings.cleared') : v.prep;
    case 'editors':
      return editorsLabel(t, v.editors);
  }
}

/** Who may change what they sell (B10), as the words the console shows. */
function editorsLabel(t: T, editors: CatalogEditors): string {
  switch (editors) {
    case 'owner':
      return t('setup.settings.editors.owner');
    case 'owner_and_staff':
      return t('setup.settings.editors.ownerAndStaff');
  }
}

/**
 * The enums, as sentences rather than tokens. `switch` rather than a computed
 * key, so a value added to the enum later fails the build instead of printing
 * `undefined`.
 */
function paymentLabel(t: T, payment: string): string {
  switch (payment) {
    case 'at_pickup':
      return t('settings.payment.atPickup');
    case 'prepay':
      return t('settings.payment.prepay');
    default:
      return payment;
  }
}

function timingLabel(t: T, timing: string): string {
  switch (timing) {
    case 'asap':
      return t('settings.timing.asap');
    case 'scheduled':
      return t('settings.timing.scheduled');
    case 'either':
      return t('settings.timing.either');
    default:
      return timing;
  }
}

function confirmationLabel(t: T, confirmation: string): string {
  switch (confirmation) {
    case 'staff_confirm':
      return t('settings.confirmation.staff');
    case 'auto_confirm':
      return t('settings.confirmation.auto');
    default:
      return confirmation;
  }
}

function bookingLabel(t: T, booking: string): string {
  switch (booking) {
    case 'open':
      return t('settings.booking.open');
    case 'trusted':
      return t('settings.booking.trusted');
    case 'request':
      return t('settings.booking.request');
    default:
      return booking;
  }
}
